
// Importing the styled components
import {
  Container,
  DeviationWrapper,
  LeftPanel,
  RightPanel,
  ReferencePositionWrapper,
  InputTypeWrapper,
  RadioGroup,
  RadioButton,
  PositionInputWrapper,
  ButtonWrapper,
  GridContainer,
  PositionRow,
  Label,
  Value,
  SectionTitle,
  StyledButton,
  StyledButtonAlt,
  CurrentPositionWrapper,
  FixedPositionRow,
  Unit,
  StyledLabel,
  StyledSelect,
  StyledOption,
  ZoomButton,
} from './DeviationComponentStyled';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  currentPositionAtom,
  referencePositionAtom,
  deviationAtom,
} from '../../recoil/deviation';
import { accessTokenAtom } from "../../recoil/auth";
import { selectedCategoryAtom } from "../../recoil";
import React, { useState, useEffect } from "react";
import { FaSyncAlt, FaMapMarkerAlt, FaSearchPlus, FaSearchMinus } from 'react-icons/fa';
import { useQuery } from "@tanstack/react-query";
import io from "socket.io-client";
import { SOCKET_URL } from "../../constants";
import { getDevices } from '../../apis/api/Device/getDevices';
import { getError, setInputXYZ } from '../../library/deviationState';
import { xyz2gd, gd2xyz, degree2dms, } from '../../library/deviation';
import { Scatter } from 'react-chartjs-2';
import styled from 'styled-components';  // Import styled-components

import { Chart as ChartJS, LinearScale, PointElement, Tooltip, Legend, Title, Filler } from 'chart.js';
ChartJS.register(LinearScale, PointElement, Tooltip, Legend, Title, Filler);

// 원을 그리기 위한 커스텀 플러그인
const drawCirclesPlugin = {
  id: 'drawCircles',
  afterDraw: (chart) => {
    const ctx = chart.ctx;
    const xCenter = chart.scales.x.getPixelForValue(0);
    const yCenter = chart.scales.y.getPixelForValue(0);
    const radiusStep = chart.scales.x.getPixelForValue(5) - xCenter; // 5cm 거리당 픽셀 수 계산

    ctx.save();
    ctx.strokeStyle = '#007bff'; // 원의 색상
    ctx.lineWidth = 1.5; // 원의 선 두께

    // 그리드에 맞춰 원을 그림
    ctx.beginPath(); // 새 경로 시작
    ctx.arc(xCenter, yCenter, radiusStep * 1, 0, 2 * Math.PI);
    ctx.stroke();
    ctx.restore();
  },
};

const donutMarkerPlugin = {
  id: 'donutMarkerPlugin',
  afterDraw: (chart) => {
    const ctx = chart.ctx;
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      meta.data.forEach((point, index) => {
        if (dataset.pointStyle[index] === 'donut') {
          const x = point.x;
          const y = point.y;
          const outerRadius = point.radius;
          const innerRadius = outerRadius / 2; // Donut hole radius

          // Draw the outer circle (donut shape)
          ctx.beginPath();
          ctx.arc(x, y, outerRadius, 0, Math.PI * 2);
          ctx.fillStyle = dataset.backgroundColor[index];
          ctx.fill();
          ctx.stroke();

          // Clear the inner circle to make it transparent (create the donut hole)
          ctx.save(); // Save the current state before clearing
          ctx.globalCompositeOperation = 'destination-out'; // Set operation to make it transparent
          ctx.beginPath();
          ctx.arc(x, y, innerRadius, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore(); // Restore to the previous state to stop clearing
        }
      });
    });
  },
};


const DeviationComponent = () => {
  const [inputType, setInputType] = useState('XYZ');
  const [referencePosition, setReferencePosition] = useRecoilState(referencePositionAtom);
  const [currentPosition, setCurrentPosition] = useRecoilState(currentPositionAtom); // Current Position이 실시간으로 업데이트되도록 설정
  const [sortedData, setSortedData] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState({});
  const [filteredDataArray, setFilteredData] = useState([]);
  
  // Deviation 좌표 관리
  const [deviation, setDeviation] = useRecoilState(deviationAtom);
  const [deviationPoints, setDeviationPoints] = useState([]);
  const [deviationPoint, setDeviationPoint] = useState({ x: 0, y: 0, q: '' });

  const [zoomLevel, setZoomLevel] = useState(2); // 기본 zoomLevel을 2cm로 설정
  const maxZoomLevel = 5000;  // Define maximum zoom level
  const zoomPercentage = (zoomLevel / maxZoomLevel) * 100;  // Calculate zoom percentage
  const scatterData = {
    datasets: [
      {
        label: 'Deviation Points',
        data: deviationPoints.map((point) => ({
          x: parseFloat(point.x),
          y: parseFloat(point.y),
        })),
        borderColor: 'rgba(0, 123, 255, 0.6)', // Semi-transparent blue border
        backgroundColor: deviationPoints.map((point, index) => {
          if (index === deviationPoints.length - 1) {
            return 'rgba(0, 0, 255, 0.9)'; // Dark blue for the current point
          }
          switch (point.q) {
            case 'fix': return 'rgba(0, 255, 0, 0.6)'; // Green for fix
            case 'float': return 'rgba(255, 255, 0, 0.6)'; // Yellow for float
            case 'dgps': return 'rgba(255, 165, 0, 0.6)'; // Orange for DGPS
            default: return 'rgba(255, 0, 0, 0.6)'; // Red for standalone and others
          }
        }),
        showLine: false,
        pointStyle: deviationPoints.map((_, index) => 'circle'),
        pointRadius: deviationPoints.map((_, index) =>
          index === deviationPoints.length - 1 ? 11 : 6 // Set a larger radius for the current point
        ),
      },
    ],
  };

  const getGridStepSize = (zoomLevel) => {
    if (zoomLevel <= 2) {
      return zoomLevel / 2; // 2x2 grid
    } else if (zoomLevel <= 10) {
      return zoomLevel / 2; // 4x4 grid
    } else if (zoomLevel <= 50) {
      return zoomLevel / 2; // 4x4 grid
    } else if (zoomLevel >= 100) {
      return zoomLevel / 3; // 6x6 grid
    } else {
      return zoomLevel / 3; // Default case, 8x8 grid
    }
  };

const scatterOptions = {
  scales: {
    x: {
      type: 'linear',
      position: 'bottom',
      min: -zoomLevel,
      max: zoomLevel,
      title: {
        display: true,
        text: `Δ E [ cm ]`,
        font: {
          size: 16,
        },
      },
      grid: {
        color: (context) => (context.tick.value === 0 ? '#dcdcdc' : '#f0f0f0'),
        lineWidth: (context) => (context.tick.value === 0 ? 1.5 : 1),
        stepSize: getGridStepSize(zoomLevel),
      },
      ticks: {
        stepSize: getGridStepSize(zoomLevel),
        callback: function (value) {
          return zoomLevel >= 2 ? `${Math.round(value)} cm` : `${value.toFixed(1)} cm`;
        },
        font: {
          size: 22,
          weight: 'bold',
        },
      },
    },
    y: {
      min: -zoomLevel,
      max: zoomLevel,
      title: {
        display: true,
        text: `Δ N [ cm ]`,
        font: {
          size: 16,
        },
      },
      grid: {
        color: (context) => (context.tick.value === 0 ? '#dcdcdc' : '#f0f0f0'),
        lineWidth: (context) => (context.tick.value === 0 ? 1.5 : 1),
        stepSize: getGridStepSize(zoomLevel),
      },
      ticks: {
        stepSize: getGridStepSize(zoomLevel),
        callback: function (value) {
          // zoomLevel이 2 이하일 경우 소수점 표시, 그 외엔 정수로 표시
          return zoomLevel >= 2 ? `${Math.round(value)} cm` : `${value.toFixed(1)} cm`;
        },
        font: {
          size: 22,
          weight: 'bold',
        },
      },
    },
  },
  plugins: {
    legend: {
      display: false, // Hide the legend
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const { x, y } = context.raw;
          return `x: ${x.toFixed(2)} cm, y: ${y.toFixed(2)} cm`;
        },
      },
    },
    title: {
      display: true,
      font: {
        size: 18,
        weight: 'bold',
      },
    },
    drawCirclesPlugin: true,
    donutMarkerPlugin: true, 
  },
  maintainAspectRatio: false,
  animation: false,
};
  
  // Zoom In/Out Functionality
  const handleZoomIn = () => {
    setZoomLevel((prev) => {
      if (prev >= 1000) {
        return prev + 250;
      } else if (prev >= 500) {
        return prev + 100; // 500cm 이상일 때는 100cm씩 확대
      } else if (prev >= 100) {
        return prev + 50; // 100cm ~ 500cm에서는 50cm씩 확대
      } else if (prev < 10) {
        return 10; // 10cm 미만일 때는 10으로 설정
      } else {
        return prev + 10; // 100cm 이하일 때는 10cm씩 확대
      }
    });
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => {
      if (prev > 1000) {
        return prev - 250; // 500cm 이상일 때는 100cm씩 축소
      } else if (prev > 500) {
        return prev - 100; // 500cm 이상일 때는 100cm씩 축소
      } else if (prev > 100) {
        return prev - 50; // 100cm ~ 500cm에서는 50cm씩 축소
      } else if (prev <= 10) {
        return 2;
      } else {
        return prev - 10; // 100cm 이하일 때는 10cm씩 축소
      }
    });
  };
  

  // Reference Lat/Lng 상태 추가
  const [referenceLatDegree, setReferenceLatDegree] = useState(0);
  const [referenceLngDegree, setReferenceLngDegree] = useState(0);
  const [referenceHeight, setReferenceHeight] = useState(0);
  const [referenceLatDMS, setReferenceLatDMS] = useState([0, 0, 0]);
  const [referenceLngDMS, setReferenceLngDMS] = useState([0, 0, 0]);
  
  const [selectedDeviceTime, setSelectedDeviceTime] = useState("N/A");
  const accessToken = useRecoilValue(accessTokenAtom);

  // Function to handle changing the input type
const handleInputTypeChange = (type) => {
  setInputType(type);
  if (type === "LLH(Degree)") {
    const llh = xyz2gd(referencePosition.x, referencePosition.y, referencePosition.z);
    setReferenceLatDegree(llh[0].toFixed(8));
    setReferenceLngDegree(llh[1].toFixed(8));
    setReferenceHeight(llh[2].toFixed(3));
  } else if (type === "LLH(DMS)") {
    const llh = xyz2gd(referencePosition.x, referencePosition.y, referencePosition.z);
    const dms = degree2dms(llh[0], llh[1]);
    setReferenceLatDMS(dms.lat);
    setReferenceLngDMS(dms.lng);
    setReferenceHeight(llh[2].toFixed(3));
  }
};

// Function to set current position as reference position
const setCurrentToReference = () => {
  setReferencePosition(currentPosition);

  if (inputType === 'XYZ') {
    // No need to convert, already in XYZ format
    setReferencePosition(currentPosition);
  } else if (inputType === 'LLH(Degree)') {
    // Convert currentPosition (XYZ) to LLH(Degree)
    const llh = xyz2gd(currentPosition.x, currentPosition.y, currentPosition.z);
    setReferenceLatDegree(llh[0].toFixed(8));
    setReferenceLngDegree(llh[1].toFixed(8));
    setReferenceHeight(llh[2].toFixed(3));
  } else if (inputType === 'LLH(DMS)') {
    // Convert currentPosition (XYZ) to LLH(Degree) and then to LLH(DMS)
    const llh = xyz2gd(currentPosition.x, currentPosition.y, currentPosition.z);
    const dms = degree2dms(llh[0], llh[1]);
    setReferenceLatDMS(dms.lat);
    setReferenceLngDMS(dms.lng);
    setReferenceHeight(llh[2].toFixed(3)); // Height remains the same
  }
};

const isDataInvalid = (item) => {
  return (
    isNaN(item.lat) || item.lat === null || item.lat === undefined || item.lat === 0 ||
    isNaN(item.lon) || item.lon === null || item.lon === undefined || item.lon === 0 ||
    isNaN(item.alt) || item.alt === null || item.alt === undefined || item.alt === 0 ||
    isNaN(item.geoidal) || item.geoidal === null || item.geoidal === undefined || item.geoidal === 0 ||
    isNaN(new Date(item.createdAt).getTime()) || item.createdAt === null || item.createdAt === undefined ||
    item.quality === null || item.quality === undefined || item.quality === 0
  );
};

  // 유저에 맞는 실시간 장치 데이터 받아오는 웹소켓
  useEffect(() => {
    const socket = io(`${SOCKET_URL}`, {
      transports: ["websocket"],
      auth: { accessToken: accessToken },
    });
  
    socket.on("real_time_device", (data) => {
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
  
      setFilteredData((prevData) => {
        const dataMap = new Map(prevData.map(device => [device.serial, device]));
  
        Object.keys(parsedData).forEach((key) => {
          const dataObject = parsedData[key];
          dataObject.lat = parseFloat(dataObject.lat);
          dataObject.lon = parseFloat(dataObject.lon);
          dataObject.alt = parseFloat(dataObject.alt);
          dataObject.createdAt = parseFloat(dataObject.createdAt);
          dataObject.serial = key;
  
          // Check if the dataObject is valid using the isDataInvalid function

          if (isDataInvalid(dataObject)) {
            return; // Skip invalid data
          }
            
          dataMap.set(key, dataObject);
        });

        return Array.from(dataMap.values());
      });

      
  
      // Check if the selectedDevice is valid before updating Current Position
      if (selectedDevice) {
        const selectedDeviceData = parsedData[selectedDevice.serial];
        setSelectedDeviceTime(selectedDeviceData?.createdAt || "N/A");
        
        // Ensure selectedDeviceData is valid
        if (selectedDeviceData && !isDataInvalid(selectedDeviceData)) {
          const [x, y, z] = gd2xyz(
            selectedDeviceData.lat,
            selectedDeviceData.lon,
            selectedDeviceData.alt + selectedDeviceData.geoidal
          );
  
          setCurrentPosition({
            x: x.toFixed(8),
            y: y.toFixed(8),
            z: z >= 0 ? z.toFixed(3) : 0,
            q: selectedDeviceData.quality || 0, // 퀄리티
          });
        } else {
          setCurrentPosition({
            x: 0,
            y: 0,
            z: 0,
            q: 0,          });
        }
      } else {
        setCurrentPosition({
          x: 0,
          y: 0,
          z: 0,
          q: 0,
        });
      }
    });
  
    return () => {
      socket.disconnect();
    };
  }, [accessToken, selectedDevice]);
  

  // Deviation 계산 및 그리드 상 점 위치 설정
  useEffect(() => {
    const currentLLH = xyz2gd(currentPosition.x, currentPosition.y, currentPosition.z);
    const deviationValues = getError(
      currentLLH[0], currentLLH[1], currentLLH[2],
      referencePosition.x, referencePosition.y, referencePosition.z
    );

    setDeviation({
      e: deviationValues[1],
      n: deviationValues[0],
      v: deviationValues[2],
    });
    

    const newPoint = {
      x: (deviationValues[1] * 100).toFixed(3),
      y: (deviationValues[0] * 100).toFixed(3),
      q: currentPosition?.q || 'standalone', // Get the quality of the device
    };

    setDeviationPoints(prev => [...prev, newPoint]);
    setDeviationPoint(newPoint);
  }, [currentPosition, referencePosition]);

  const handleDeviceChange = (e) => {
    const deviceSerial = e.target.value;
  
    const selectedDeviceObj = filteredDataArray.find(device => device.serial === deviceSerial);
    setSelectedDevice(selectedDeviceObj || {});
  
    if (!deviceSerial) {
      setReferencePosition({ x: 0, y: 0, z: 0 });
      setReferenceLatDegree(0);
      setReferenceLngDegree(0);
      setReferenceHeight(0);
      setReferenceLatDMS([0, 0, 0]);
      setReferenceLngDMS([0, 0, 0]);
    }
  };

  const handleReferenceChange = (e) => {
    const { name, value } = e.target;
    const updatedPosition = {
      ...referencePosition,
      [name]: parseFloat(value) || 0,
    };
    
    setReferencePosition(updatedPosition);

    const xyzPosition = setInputXYZ(referencePosition, {
      referenceX: updatedPosition.x,
      referenceY: updatedPosition.y,
      referenceZ: updatedPosition.z,
    });

    setReferencePosition((prev) => ({
      ...prev,
      x: xyzPosition[0],
      y: xyzPosition[1],
      z: xyzPosition[2],
    }));
  };

  const updateCurrentPositionView = () => {
    if (inputType === 'XYZ') {
      return (
        <>
          <FixedPositionRow>
            <Label>X:</Label>
            <Value>{(parseFloat(currentPosition.x)?.toFixed(3) || "0")}</Value><Unit>m</Unit>

          </FixedPositionRow>
          <FixedPositionRow>
            <Label>Y:</Label>
            <Value>{(parseFloat(currentPosition.y)?.toFixed(3) || "0")}</Value><Unit>m</Unit>
          </FixedPositionRow>
          <FixedPositionRow>
            <Label>Z:</Label>
            <Value>{(parseFloat(currentPosition?.z >= 0 ? currentPosition.z : 0)?.toFixed(3) || "0")}</Value><Unit>m</Unit>
          </FixedPositionRow>
          <FixedPositionRow>
            <Label>Quality:</Label>
            <Value style={{ fontWeight: "bold", fontSize: "20px" }}>
              {currentPosition.q ? currentPosition.q.toUpperCase() : "N/A"}
            </Value>
          </FixedPositionRow>
        </>
      );
    } else if (inputType === 'LLH(Degree)') {
      const llh = xyz2gd(currentPosition.x, currentPosition.y, currentPosition.z);
      return (
        <>
          <FixedPositionRow>
            <Label>Latitude:</Label>
            <Value>{llh[0].toFixed(8)}</Value><Unit>°</Unit>
          </FixedPositionRow>
          <FixedPositionRow>
            <Label>Longitude:</Label>
            <Value>{llh[1].toFixed(8)}</Value><Unit>°</Unit>
          </FixedPositionRow>
          <FixedPositionRow>
            <Label>Height:</Label>
            <Value>{llh[2] >= 0 ? llh[2].toFixed(3) : "0"}</Value><Unit>m</Unit> {/* Height 값이 마이너스일 경우 0으로 */}
          </FixedPositionRow>
        </>
      );
    } else if (inputType === 'LLH(DMS)') {
      const llh = xyz2gd(currentPosition.x, currentPosition.y, currentPosition.z);
      const dms = degree2dms(llh[0], llh[1]);
      return (
        <>
          <FixedPositionRow>
            <Label>Latitude:</Label>
            <Value>{dms.lat[0]}° {dms.lat[1]}' {dms.lat[2]}"</Value>
          </FixedPositionRow>
          <FixedPositionRow>
            <Label>Longitude:</Label>
            <Value>{dms.lng[0]}° {dms.lng[1]}' {dms.lng[2]}"</Value>
          </FixedPositionRow>
          <FixedPositionRow>
            <Label>Height:</Label>
            <Value>{llh[2] >= 0 ? llh[2].toFixed(3) : "0"}</Value><Unit>m</Unit> {/* Height 값이 마이너스일 경우 0으로 */}
          </FixedPositionRow>
        </>
      );
    }
  };

  return (
    <Container>
      <DeviationWrapper>
        <LeftPanel>
          <SectionTitle>Reference Position</SectionTitle>
          <ReferencePositionWrapper>
          {/* Device Dropdown */}
          <StyledLabel>Device:</StyledLabel>
            <StyledSelect value={selectedDevice?.serial || ''} onChange={handleDeviceChange}>
              <option value="">--Select Device--</option>
              {filteredDataArray &&
                filteredDataArray.map(device => (
                  <StyledOption key={device.serial} value={device.serial}>
                    {device.alias || device.name || device.serial || " - "}
                  </StyledOption>
                ))}
            </StyledSelect>
            <InputTypeWrapper>
              <RadioGroup>
                <RadioButton>
                  <input
                    type="radio"
                    value="XYZ"
                    checked={inputType === 'XYZ'}
                    onChange={() => handleInputTypeChange('XYZ')}
                  />
                  <span>XYZ</span>
                </RadioButton>
                <RadioButton>
                  <input
                    type="radio"
                    value="LLH(Degree)"
                    checked={inputType === 'LLH(Degree)'}
                    onChange={() => handleInputTypeChange('LLH(Degree)')}
                  />
                  <span>LLH (Degree)</span>
                </RadioButton>
                <RadioButton>
                  <input
                    type="radio"
                    value="LLH(DMS)"
                    checked={inputType === 'LLH(DMS)'}
                    onChange={() => handleInputTypeChange('LLH(DMS)')}
                  />
                  <span>LLH (DMS)</span>
                </RadioButton>
              </RadioGroup>
            </InputTypeWrapper>

            {inputType === "XYZ" && (
              <PositionInputWrapper>
                <label>X [m]:</label>
                <input
                  type="number"
                  name="x"
                  value={parseFloat(referencePosition.x).toFixed(3) || 0}
                  onChange={handleReferenceChange}
                  step="0.001" // Set step for more precise input control
                />
                <label>Y [m]:</label>
                <input
                  type="number"
                  name="y"
                  value={parseFloat(referencePosition.y).toFixed(3) || 0}
                  onChange={handleReferenceChange}
                  step="0.001" // Set step for more precise input control
                />
                <label>Z [m]:</label>
                <input
                  type="number"
                  name="z"
                  value={ referencePosition.z >= 0 ? parseFloat(referencePosition.z).toFixed(3) : 0}
                  onChange={handleReferenceChange}
                  step="0.001" // Set step for more precise input control
                />
              </PositionInputWrapper>
            )}

            {/* Other input types for LLH (Degree) and LLH (DMS) */}
            {inputType === "LLH(Degree)" && (
              <PositionInputWrapper>
                <label>Latitude [°]:</label>
                <input
                  type="number"
                  value={referenceLatDegree || 0}
                  readOnly
                />
                <label>Longitude [°]:</label>
                <input
                  type="number"
                  value={referenceLngDegree || 0}
                  readOnly
                />
                <label>Height [m]:</label>
                <input
                  type="number"
                  value={referenceHeight >= 0 ? referenceHeight : 0}
                  readOnly
                />
              </PositionInputWrapper>
            )}

            {inputType === "LLH(DMS)" && (
              <PositionInputWrapper>
                <label>Latitude (DMS):</label>
                <input
                  type="number"
                  value={referenceLatDMS[0] || 0}
                  readOnly
                />
                <input
                  type="number"
                  value={referenceLatDMS[1] || 0}
                  readOnly
                />
                <input
                  type="number"
                  value={referenceLatDMS[2] || 0}
                  readOnly
                />
                <label>Longitude (DMS):</label>
                <input
                  type="number"
                  value={referenceLngDMS[0] || 0}
                  readOnly
                />
                <input
                  type="number"
                  value={referenceLngDMS[1] || 0}
                  readOnly
                />
                <input
                  type="number"
                  value={referenceLngDMS[2] || 0}
                  readOnly
                />
                <label>Height [m]:</label>
                <input
                  type="number"
                  value={referenceHeight >= 0 ? referenceHeight : 0}
                  readOnly
                />
              </PositionInputWrapper>
            )}

            <ButtonWrapper>
              <StyledButtonAlt onClick={setCurrentToReference}>
                <FaMapMarkerAlt size={18} /> Set Current
              </StyledButtonAlt>
              
              <StyledButton onClick={() => setDeviationPoints([])}>
                <FaSyncAlt size={18} /> Clear
              </StyledButton>
            </ButtonWrapper>
          </ReferencePositionWrapper>
        </LeftPanel>

        <GridContainer>
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px', gap: '10px' }}>
                <ZoomButton onClick={handleZoomIn} disabled={zoomLevel >= 5000}>
                  <FaSearchMinus />
                </ZoomButton>
                <ZoomButton onClick={handleZoomOut} disabled={zoomLevel <= 2}>
                  <FaSearchPlus />
                </ZoomButton>
              </div>
              <ProgressBarContainer>
                <ProgressBar>
                  <ProgressFill style={{ width: `${zoomPercentage}%` }} />
                </ProgressBar>
                <ZoomLabel>{zoomPercentage.toFixed(0)}%</ZoomLabel>
              </ProgressBarContainer>
            </div>

            {/* Scatter Chart */}
            <div style={{ width: '600px', height: '600px', margin: '20px auto' }}>
              <Scatter data={scatterData} options={scatterOptions} plugins={[drawCirclesPlugin, donutMarkerPlugin]} />
            </div>
          </div>
        </GridContainer>

        <RightPanel>
          <SectionTitle>
            Current Position           
            {/* <MarkerLegendContainer>
              <MarkerImage src={currentMarker} alt="Current Marker" />
              <MarkerLabel>: 현재 위치 표시 아이콘</MarkerLabel>
            </MarkerLegendContainer> */}
          </SectionTitle>
          
          <Timestamp>{selectedDeviceTime}</Timestamp>

          <CurrentPositionWrapper>
            {updateCurrentPositionView()}
          </CurrentPositionWrapper>
          <SectionTitle>Deviation</SectionTitle>
          <CurrentPositionWrapper>
            <PositionRow>
              <Label>ΔE:</Label>
              <Value strong={true}>
                {selectedDevice && referencePosition.x && referencePosition.y && referencePosition.z &&
                  (selectedDevice.quality === 'fix' || selectedDevice.quality === 'float')
                  ? (parseFloat(deviation.e) * 100).toFixed(1)
                  : selectedDevice && referencePosition.x && referencePosition.y && referencePosition.z
                  ? parseFloat(deviation.e).toFixed(2)
                  : "N/A"}
              </Value>
              {selectedDevice && referencePosition.x && referencePosition.y && referencePosition.z &&
                (selectedDevice.quality === 'fix' || selectedDevice.quality === 'float') ? (
                <Unit>cm</Unit>
              ) : selectedDevice && referencePosition.x && referencePosition.y && referencePosition.z ? (
                <Unit>m</Unit>
              ) : null}
            </PositionRow>
            <PositionRow>
              <Label>ΔN:</Label>
              <Value strong={true}>
                {selectedDevice && referencePosition.x && referencePosition.y && referencePosition.z &&
                  (selectedDevice.quality === 'fix' || selectedDevice.quality === 'float')
                  ? (parseFloat(deviation.n) * 100).toFixed(1)
                  : selectedDevice && referencePosition.x && referencePosition.y && referencePosition.z
                  ? parseFloat(deviation.n).toFixed(2)
                  : "N/A"}
              </Value>
              {selectedDevice && referencePosition.x && referencePosition.y && referencePosition.z &&
                (selectedDevice.quality === 'fix' || selectedDevice.quality === 'float') ? (
                <Unit>cm</Unit>
              ) : selectedDevice && referencePosition.x && referencePosition.y && referencePosition.z ? (
                <Unit>m</Unit>
              ) : null}
            </PositionRow>

            <PositionRow>
              <Label>ΔV:</Label>
              <Value strong={true}>
                {selectedDevice && referencePosition.x && referencePosition.y && referencePosition.z &&
                  (selectedDevice.quality === 'fix' || selectedDevice.quality === 'float')
                  ? (parseFloat(deviation.v) * 100).toFixed(1)
                  : selectedDevice && referencePosition.x && referencePosition.y && referencePosition.z
                  ? parseFloat(deviation.v).toFixed(2)
                  : "N/A"}
              </Value>
              {selectedDevice && referencePosition.x && referencePosition.y && referencePosition.z &&
                (selectedDevice.quality === 'fix' || selectedDevice.quality === 'float') ? (
                <Unit>cm</Unit>
              ) : selectedDevice && referencePosition.x && referencePosition.y && referencePosition.z ? (
                <Unit>m</Unit>
              ) : null}
            </PositionRow>
            <LegendContainer>
              <LegendItem>
                <ColorBlock color="rgba(0, 255, 0, 0.6)" />
                <LegendLabel>Fix</LegendLabel>
              </LegendItem>
              <LegendItem>
                <ColorBlock color="rgba(255, 255, 0, 0.6)" />
                <LegendLabel>Float</LegendLabel>
              </LegendItem>
              <LegendItem>
                <ColorBlock color="rgba(255, 165, 0, 0.6)" />
                <LegendLabel>DGPS</LegendLabel>
              </LegendItem>
              <LegendItem>
                <ColorBlock color="rgba(255, 0, 0, 0.6)" />
                <LegendLabel>Standalone</LegendLabel>
              </LegendItem>
            </LegendContainer>
          </CurrentPositionWrapper>
        </RightPanel>
      </DeviationWrapper>
    </Container>
  );
}

// Styled Components for Progress Bar
const ProgressBarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
  width: 100%;
`;

const ProgressBar = styled.div`
  position: relative;
  width: 80%;
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: #007bff;
  transition: width 0.3s ease;
`;

const ZoomLabel = styled.span`
  margin-left: 10px;
  font-size: 14px;
  color: #333;
`;

const LegendContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 20px;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 8px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ColorBlock = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background-color: ${(props) => props.color};
`;

const LegendLabel = styled.span`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`;

const MarkerLegendContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`;

const MarkerImage = styled.img`
  width: 20px;
  height: 20px;
`;

const MarkerLabel = styled.span`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`;

const Timestamp = styled.h2`
  font-size: 24px;
  color: #0056b3;
  font-weight: 600;
  text-align: center;
  margin-top: 10px;
  letter-spacing: 0.5px;
  background: linear-gradient(90deg, #e0f7ff, #cceeff);
  padding: 12px 25px;
  border-radius: 12px;
  box-shadow: 0px 4px 12px rgba(0, 85, 179, 0.2);
  transition: all 0.3s ease-in-out;
  &:hover {
    background: linear-gradient(90deg, #cceeff, #b3e0ff);
    box-shadow: 0px 6px 15px rgba(0, 85, 179, 0.3);
  }
`;

export default DeviationComponent;
